/* LAV TDMS — SmartTable: an "intelligent" data table with group-by,
   per-column value filtering, multi-sort and optional row selection.
   Faithfully ported from the prototype's app/SmartTable.jsx (window.APP).
   Drop-in for the dense register tables. Inline styles kept verbatim. */
import {
  type CSSProperties,
  type ReactNode,
  Fragment,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useVirtualizer, type VirtualItem } from '@tanstack/react-virtual'
import { Icon } from '@/ds/icons'
import { clickable } from './a11y'
import { Input, Checkbox } from './forms'
import { applyHidden, applyOrder, useTableLayout } from './table-layout'
import { useDensity, useDropFlip, useDropMaxHeight } from './responsive'
import type { CellValue } from './data-table'
import { t, type Lang } from '@/i18n/lang'

/** Единица показа: заголовок группы или строка. Виртуализатор про группы не знает. */
type VisualItem<R> =
  | { kind: 'group'; key: string; g: { v: CellValue; rows: R[] } }
  | { kind: 'row'; r: R }

/** С какого числа единиц показа таблица переходит на виртуализацию. */
const VIRTUAL_FROM = 60

/* Высота строки — токен раскладки, а не число здесь: плотность одна на всё
   приложение, и вторая копия числа разошлась бы с CSS при первой же правке.
   Виртуализатору нужно именно число, поэтому токен читается с корня документа.
   Запасное значение — для среды без стилей (jsdom в тестах): виртуализатор
   переживёт неточную оценку, деление на пустую строку — нет. */
function rowHeight(compact: boolean): number {
  const px = parseFloat(getComputedStyle(document.documentElement).getPropertyValue(compact ? '--row-h-compact' : '--row-h'))
  return px > 0 ? px : compact ? 30 : 36
}


export interface SmartColumn<R = unknown> {
  key: string
  header: ReactNode
  render?: (r: R) => ReactNode
  width?: number | string
  align?: 'left' | 'right' | 'center'
  mono?: boolean
  nowrap?: boolean
  groupable?: boolean
  filterable?: boolean
  sortable?: boolean
  value?: (r: R) => CellValue
  valueLabel?: (v: CellValue) => ReactNode
  /* Значения отбора, известные заранее: статусы, назначения, коды. Без них
     список в шапке собирается из загруженной страницы, а при отборе на сервере
     страница уже сужена — и выбранное значение остаётся в списке единственным,
     то есть отбор нечем расширить. */
  filterValues?: string[]
  /* Счётчики значений, посчитанные сервером по ВСЕМУ набору (фасеты). Без них
     числа в списке отбора собираются с загруженной страницы: «ПроектСталь 33»
     означало «33 из ста показанных», а не «33 в реестре», и по такому числу
     писали письма. Заданы — страница не считается вовсе. */
  filterCounts?: Record<string, number>
  /* Столбец, по которому опознают запись: номер документа, наименование
     организации. Прятать его нельзя — таблица без него перестаёт быть таблицей
     этих записей. */
  required?: boolean
}

const val = <R,>(col: SmartColumn<R>, r: R): CellValue =>
  col.value ? col.value(r) : ((r as Record<string, CellValue>)[col.key] ?? null)
const lbl = <R,>(col: SmartColumn<R>, v: CellValue): ReactNode =>
  col.valueLabel ? col.valueLabel(v) : v == null || v === '' ? '—' : v

function useOutside(ref: React.RefObject<HTMLElement | null>, onClose: () => void) {
  useEffect(() => {
    function h(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose()
    }
    document.addEventListener('mousedown', h)
    return () => document.removeEventListener('mousedown', h)
  }, [onClose, ref])
}

/** Ширина панели отбора. Нужна и в стиле, и до отрисовки — чтобы знать, влезет ли. */
const FILTER_W = 240

function FilterPopover<R>({
  col,
  rows,
  active,
  onApply,
  onClose,
  lang,
}: {
  col: SmartColumn<R>
  rows: R[]
  active: Set<string> | undefined
  onApply: (set: Set<string> | null) => void
  onClose: () => void
  lang: Lang
}) {
  const ref = useRef<HTMLDivElement>(null)
  useOutside(ref, onClose)
  /* Заголовок столбца бывает и в нижней половине экрана: список значений
     обрезается низом окна, а под ним лежат «Сброс» и «Применить». */
  const maxH = useDropMaxHeight(ref)
  /* И в правой её половине: у последних столбцов реестра панель раскрывается
     от левого края заголовка вправо и уезжает за край экрана. */
  const flip = useDropFlip(ref, FILTER_W)
  const [q, setQ] = useState('')
  const distinct = useMemo(() => {
    const m = new Map<string, { v: CellValue; n: number | null }>()
    // Известные значения идут первыми и остаются в списке, даже когда на
    // странице их нет: количество у них неизвестно, а выбрать их нужно.
    ;(col.filterValues ?? []).forEach((v) => m.set(v, { v, n: null }))
    if (col.filterCounts) {
      /* Счёт с сервера по всему набору. Страница при этом не считается вовсе:
         подмешать к серверным числам числа сотни загруженных строк значило бы
         получить третье число, не равное ни тому, ни другому. */
      for (const [v, n] of Object.entries(col.filterCounts)) m.set(v, { v, n })
    } else {
      rows.forEach((r) => {
        const v = val(col, r)
        const k = v == null ? '' : String(v)
        if (!m.has(k)) m.set(k, { v, n: 0 })
        const cell = m.get(k)!
        cell.n = (cell.n ?? 0) + 1
      })
    }
    return [...m.values()].sort((a, b) => String(lbl(col, a.v)).localeCompare(String(lbl(col, b.v)), 'ru'))
  }, [rows, col])
  const [sel, setSel] = useState<Set<string>>(active ? new Set(active) : new Set(distinct.map((d) => String(d.v))))
  const filtered = distinct.filter((d) => !q || String(lbl(col, d.v)).toLowerCase().includes(q.toLowerCase()))
  const toggle = (k: string) => {
    const n = new Set(sel)
    if (n.has(k)) n.delete(k)
    else n.add(k)
    setSel(n)
  }
  const all = sel.size === distinct.length
  return (
    <div
      ref={ref}
      style={{
        position: 'absolute',
        top: '100%',
        left: flip ? undefined : 0,
        right: flip ? 0 : undefined,
        marginTop: 4,
        width: FILTER_W,
        maxHeight: maxH,
        display: 'flex',
        flexDirection: 'column',
        zIndex: 60,
        background: 'var(--surface-raised)',
        border: '1px solid var(--border-default)',
        borderRadius: 'var(--radius-md)',
        boxShadow: 'var(--shadow-md)',
        overflow: 'hidden',
        // Всплывашка растёт от своего угла в заголовке столбца, а не возникает
        // готовой с тенью. Это не Radix, элемент монтируется по открытию —
        // поэтому вход живёт прямо здесь.
        transformOrigin: flip ? 'top right' : 'top left',
        animation: 'tooltip-in var(--duration-normal) var(--ease-out)',
      }}
    >
      <div style={{ padding: 8, borderBottom: '1px solid var(--border-subtle)' }}>
        <Input
          size="sm"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          leading={<Icon name="search" size={13} />}
          placeholder={t(lang, 'Значение…', 'Value…')}
        />
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '6px 12px',
          borderBottom: '1px solid var(--border-subtle)',
          cursor: 'pointer',
        }}
        {...clickable(() => setSel(all ? new Set() : new Set(distinct.map((d) => String(d.v)))))}
      >
        <Checkbox checked={all} indeterminate={!all && sel.size > 0} onChange={() => {}} />
        <span style={{ font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>
          {t(lang, 'Выбрать все', 'Select all')}
        </span>
      </div>
      <div style={{ maxHeight: 220, minHeight: 0, overflow: 'auto' }}>
        {filtered.map((d) => {
          const k = String(d.v)
          return (
            <label
              key={k}
              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px', cursor: 'pointer' }}
              className="tdms-rail"
            >
              <Checkbox checked={sel.has(k)} onChange={() => toggle(k)} />
              <span
                style={{
                  flex: 1,
                  minWidth: 0,
                  font: 'var(--font-ui)',
                  fontSize: 'var(--text-sm)',
                  color: 'var(--text-body)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {lbl(col, d.v)}
              </span>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5, color: 'var(--text-subtle)' }}>
                {d.n == null ? '' : d.n}
              </span>
            </label>
          )
        })}
      </div>
      <div style={{ display: 'flex', gap: 6, padding: 8, borderTop: '1px solid var(--border-subtle)' }}>
        <button
          onClick={() => {
            onApply(null)
            onClose()
          }}
          className="lav-btn-ghost"
          style={btnGhost}
        >
          {t(lang, 'Сброс', 'Reset')}
        </button>
        <button
          onClick={() => {
            onApply(sel.size === distinct.length ? null : sel)
            onClose()
          }}
          className="lav-btn-primary"
          style={btnPrimary}
        >
          {t(lang, 'Применить', 'Apply')}
        </button>
      </div>
    </div>
  )
}
const btnGhost: CSSProperties = {
  flex: 1,
  padding: '5px 8px',
  border: '1px solid var(--border-strong)',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--surface-card)',
  color: 'var(--text-muted)',
  font: 'var(--font-ui)',
  fontSize: 12,
  cursor: 'pointer',
}
const btnPrimary: CSSProperties = {
  flex: 1,
  padding: '5px 8px',
  border: '1px solid var(--accent)',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--accent)',
  color: 'var(--on-accent)',
  font: 'var(--font-ui)',
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
}

/* Меню «Колонки». Реестру нужно 1300 px по ширине, а на ноутбуке под таблицу
   остаётся 900: лишние столбцы убираются с экрана, а не уезжают за его край.
   Прячет и возвращает человек — сама таблица ничего не решает за него. */
function ColumnsPopover<R>({
  columns,
  hidden,
  onToggle,
  onReset,
  onClose,
  lang,
}: {
  /** Все столбцы в личном порядке: скрытые тоже, иначе их нечем вернуть. */
  columns: SmartColumn<R>[]
  hidden: string[]
  onToggle: (key: string) => void
  onReset: () => void
  onClose: () => void
  lang: Lang
}) {
  const ref = useRef<HTMLDivElement>(null)
  useOutside(ref, onClose)
  const maxH = useDropMaxHeight(ref)
  /* Escape слушается на документе, а не на обёртке: фокус после открытия
     остаётся на кнопке, а из меню его уводят флажки — обработчик на одном из
     них закрывал бы меню не отовсюду. */
  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', h)
    return () => document.removeEventListener('keydown', h)
  }, [onClose])
  /* Последний показанный столбец не прячется: пустая таблица читается как
     поломка, а не как настройка. */
  const lastOne = columns.length - hidden.length <= 1
  return (
    <div
      ref={ref}
      role="group"
      aria-label={t(lang, 'Колонки таблицы', 'Table columns')}
      style={{
        position: 'absolute',
        top: '100%',
        right: 0,
        marginTop: 4,
        width: 232,
        maxHeight: maxH,
        display: 'flex',
        flexDirection: 'column',
        zIndex: 60,
        background: 'var(--surface-raised)',
        border: '1px solid var(--border-default)',
        borderRadius: 'var(--radius-md)',
        boxShadow: 'var(--shadow-md)',
        overflow: 'hidden',
        transformOrigin: 'top right',
        animation: 'tooltip-in var(--duration-normal) var(--ease-out)',
      }}
    >
      <div style={{ maxHeight: 260, minHeight: 0, overflow: 'auto', padding: '4px 0' }}>
        {columns.map((c) => {
          const shown = !hidden.includes(c.key)
          const locked = !!c.required || (shown && lastOne)
          return (
            <div
              key={c.key}
              className="tdms-rail"
              style={{ padding: '5px 12px' }}
              title={
                c.required
                  ? t(lang, 'Столбец опознаёт запись — скрыть нельзя', 'Identifies the row — cannot be hidden')
                  : locked
                    ? t(lang, 'Последний столбец скрыть нельзя', 'The last column cannot be hidden')
                    : undefined
              }
            >
              <Checkbox
                checked={shown}
                disabled={locked}
                onChange={() => onToggle(c.key)}
                /* Заголовок пуст у столбцов с кнопкой действия — в списке
                   такая строка была бы флажком без подписи. */
                label={c.header === '' || c.header == null ? c.key : c.header}
                style={{ width: '100%' }}
              />
            </div>
          )
        })}
      </div>
      <div style={{ display: 'flex', padding: 8, borderTop: '1px solid var(--border-subtle)' }}>
        <button
          onClick={() => {
            onReset()
            onClose()
          }}
          className="lav-btn-ghost"
          style={btnGhost}
          title={t(lang, 'Вернуть состав, ширины и порядок столбцов', 'Restore columns, widths and order')}
        >
          {t(lang, 'Сбросить', 'Reset')}
        </button>
      </div>
    </div>
  )
}

export function SmartTable<R = unknown>({
  columns,
  rows,
  rowKey,
  compact: compactProp,
  selectedKeys,
  onToggleRow,
  onToggleAll,
  onRowClick,
  emptyText,
  loading = false,
  lang = 'ru',
  defaultGroup = null,
  name,
  sort: sortProp,
  onSort,
  colFilters,
  onFilter,
}: {
  columns: SmartColumn<R>[]
  rows: R[]
  rowKey: (r: R) => React.Key
  /* Плотность строк — общая настройка приложения (тема, язык, плотность), и
     таблицы берут её из контекста. Свойство осталось для таблицы, у которой
     плотность своя по месту, а не по выбору человека. */
  compact?: boolean
  selectedKeys?: Set<React.Key>
  onToggleRow?: (k: React.Key) => void
  onToggleAll?: (checked: boolean) => void
  onRowClick?: (r: R) => void
  emptyText?: ReactNode
  /** Данные ещё идут: вместо одной строки текста показывается заглушка таблицы. */
  loading?: boolean
  lang?: Lang
  defaultGroup?: string | null
  name?: string
  /* Порядок с сервера. Пока его не было, заголовок сортировал ЗАГРУЖЕННУЮ
     страницу: на реестре в полторы тысячи строк «по плановой дате» упорядочивало
     пятьдесят случайных строк и выглядело настоящим. Задан onSort — таблица
     только показывает стрелку и просит новый порядок, а сортирует сервер. */
  sort?: { key: string; dir: 'asc' | 'desc' } | null
  onSort?: (s: { key: string; dir: 'asc' | 'desc' } | null) => void
  /* Отбор с сервера. Пока его не было, галочки в шапке отбирали ЗАГРУЖЕННУЮ
     страницу: реестр в полторы тысячи строк показывал «три документа на
     рассмотрении» там, где их полторы сотни, и с этим числом шли на совещание.
     Задан onFilter — таблица только показывает отмеченное и просит отбор,
     а отбирает сервер. */
  colFilters?: Record<string, string[]>
  onFilter?: (key: string, values: string[] | null) => void
}) {
  const density = useDensity()
  const compact = compactProp ?? density.compact
  const [group, setGroup] = useState<string | null>(defaultGroup)
  const [ownSort, setOwnSort] = useState<{ key: string; dir: 'asc' | 'desc' } | null>(null)
  /* Порядок считает сервер, если о нём попросили: тогда локальная сортировка
     не просто лишняя — она переставляет уже упорядоченную страницу. */
  const serverSorted = typeof onSort === 'function'
  const sort = serverSorted ? (sortProp ?? null) : ownSort
  const [ownFilters, setOwnFilters] = useState<Record<string, Set<string>>>({})
  const serverFiltered = typeof onFilter === 'function'
  const outerFilters = useMemo(() => {
    const out: Record<string, Set<string>> = {}
    for (const [k, v] of Object.entries(colFilters ?? {})) if (v.length) out[k] = new Set(v)
    return out
  }, [colFilters])
  const filters = serverFiltered ? outerFilters : ownFilters
  const [openFilter, setOpenFilter] = useState<string | null>(null)
  const [openCols, setOpenCols] = useState(false)
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set())

  /* Порядок и ширины — личная раскладка; таблица без имени их не запоминает. */
  const columnKeys = useMemo(() => columns.map((c) => c.key), [columns])
  const { layout, resize, reorder, reset, toggleColumn } = useTableLayout(name, columnKeys)
  /* `ordered` — все столбцы в личном порядке, `cols` — только показываемые.
     Меню «Колонки» перечисляет первые: скрытый столбец должен быть виден в
     списке, иначе его нечем вернуть. */
  const ordered = useMemo(() => applyOrder(columns, layout.order), [columns, layout.order])
  const cols = useMemo(() => applyHidden(ordered, layout.hidden), [ordered, layout.hidden])
  /* Тяга за границу и перенос заголовка — один и тот же нажатый левый клик.
     Без этого признака браузер начинал перенос столбца, стоило потянуть за
     границу, и ширина не менялась вовсе. */
  const resizing = useRef(false)
  const [gripKey, setGripKey] = useState<string | null>(null)

  const groupables = cols.filter((c) => c.groupable)
  /* Поиск идёт по всем столбцам, а не по показываемым: отбор и порядок по
     скрытому столбцу остаются в силе, и метка отбора в панели должна называть
     его заголовком, а не падать на пустом результате. */
  const colByKey = (k: string) => columns.find((c) => c.key === k)

  const startResize = (key: string, e: React.PointerEvent<HTMLElement>) => {
    e.preventDefault()
    e.stopPropagation()
    /* Какой столбец тянут: пока идёт тяга, его граница остаётся подсвеченной,
       даже если курсор ушёл с полосы — иначе линия гаснет посреди движения. */
    setGripKey(key)
    const grip = e.currentTarget
    const th = grip.parentElement
    if (!th) return
    resizing.current = true
    const startWidth = th.offsetWidth
    const x0 = e.clientX
    /* Ширины всех столбцов на момент начала тяги: с ними таблица переходит в
       фиксированный расклад ровно в том виде, в каком её и видели. */
    const seed: Record<string, number> = {}
    th.parentElement?.querySelectorAll<HTMLElement>('th[data-col]').forEach((cell) => {
      const col = cell.dataset.col
      if (col) seed[col] = cell.offsetWidth
    })
    /* Захват указателя — удобство, а не условие тяги: он бросает, если указателя
       с таким номером уже нет (касание прервано, кнопка отпущена мимо окна). До
       сих пор исключение уносило с собой и подписку на движение — тяга молча не
       начиналась, а подсветка границы оставалась висеть. */
    try {
      grip.setPointerCapture(e.pointerId)
    } catch {
      /* Без захвата события всё равно придут — на сам элемент и на окно. */
    }
    const move = (ev: PointerEvent) => resize(key, startWidth, ev.clientX - x0, false, seed)
    const up = (ev: PointerEvent) => {
      resize(key, startWidth, ev.clientX - x0, true, seed)
      resizing.current = false
      setGripKey(null)
      try {
        grip.releasePointerCapture(ev.pointerId)
      } catch {
        // Захвата и не было — отпускать нечего.
      }
      grip.removeEventListener('pointermove', move)
      grip.removeEventListener('pointerup', up)
      window.removeEventListener('pointerup', up)
    }
    grip.addEventListener('pointermove', move)
    grip.addEventListener('pointerup', up)
    /* Отпускание за пределами полосы — обычное дело, когда захват не состоялся:
       без него тяга не кончалась бы вовсе. */
    window.addEventListener('pointerup', up)
  }

  // filter
  // Отбирает сервер — страница пришла уже отобранной, второй проход по ней
  // только спрятал бы строки, которых в отборе нет.
  let view = serverFiltered ? rows : rows.filter((r) => {
    for (const k in filters) {
      const set = filters[k]
      if (!set) continue
      const c = colByKey(k)!
      const v = val(c, r)
      if (!set.has(v == null ? '' : String(v))) return false
    }
    return true
  })
  // sort
  if (sort && !serverSorted) {
    const c = colByKey(sort.key)!
    view = view.slice().sort((a, b) => {
      let av = val(c, a),
        bv = val(c, b)
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * (sort.dir === 'asc' ? 1 : -1)
      av = av == null ? '' : String(av)
      bv = bv == null ? '' : String(bv)
      return av.localeCompare(bv, 'ru') * (sort.dir === 'asc' ? 1 : -1)
    })
  }
  const rh = useMemo(() => rowHeight(compact), [compact])
  const hasSel = !!(selectedKeys && onToggleRow)
  const gcol = group ? colByKey(group) : null
  const totalCols = cols.length + (hasSel ? 1 : 0)

  /* Виртуализация. Причина не в скорости прокрутки, а в цене каждого узла:
     Radix при открытии модалки ставит pointer-events: none на body, свойство
     наследуемое, и вычисленный стиль становится недействительным у ВСЕЙ
     страницы. Замерено на стенде: 39 мс на списке в сто строк (5252 узла) и
     0 мс на лёгком экране — из-за этого карточка открывалась рывком.

     Список единиц показа плоский: заголовок группы и строка стоят в нём
     наравне, иначе виртуализатору пришлось бы знать про группировку. */
  const clickSort = (c: SmartColumn<R>) => {
    if (c.sortable === false) return
    const next = (s: { key: string; dir: 'asc' | 'desc' } | null) =>
      s && s.key === c.key
        ? s.dir === 'asc'
          ? { key: c.key, dir: 'desc' as const }
          : null
        : { key: c.key, dir: 'asc' as const }
    if (serverSorted && onSort) onSort(next(sort))
    else setOwnSort(next)
  }
  const applyFilter = (key: string, set: Set<string> | null) => {
    if (serverFiltered) {
      onFilter!(key, set ? [...set] : null)
      return
    }
    setOwnFilters((f) => {
      const n = { ...f }
      if (set) n[key] = set
      else delete n[key]
      return n
    })
  }

  // group rows
  let groups: { v: CellValue; rows: R[] }[] | null = null
  if (gcol) {
    const m = new Map<string, { v: CellValue; rows: R[] }>()
    view.forEach((r) => {
      const v = val(gcol, r)
      const k = v == null ? '' : String(v)
      if (!m.has(k)) m.set(k, { v, rows: [] })
      m.get(k)!.rows.push(r)
    })
    groups = [...m.values()].sort((a, b) => String(lbl(gcol, a.v)).localeCompare(String(lbl(gcol, b.v)), 'ru'))
  }

  const items = useMemo<VisualItem<R>[]>(() => {
    const out: VisualItem<R>[] = []
    if (groups) {
      for (const g of groups) {
        const gk = String(g.v)
        out.push({ kind: 'group', key: gk, g })
        if (!collapsed.has(gk)) for (const r of g.rows) out.push({ kind: 'row', r })
      }
    } else {
      for (const r of view) out.push({ kind: 'row', r })
    }
    return out
  }, [groups, view, collapsed])

  const scrollRef = useRef<HTMLDivElement>(null)
  /* Короткие таблицы не трогаем: там виртуализация ничего не экономит, а
     фиксированный расклад колонок изменил бы вид экранов без нужды. */
  const heavy = items.length > VIRTUAL_FROM

  /* Ширины колонок снимаются один раз, пока в DOM ещё все строки, и дальше
     закрепляются. Без этого расклад «по содержимому» считался бы по видимым
     строкам, и колонки прыгали бы при прокрутке. */
  const [pinned, setPinned] = useState<number[] | null>(null)
  /* Состав колонок — часть ключа: после того как одну спрятали, закреплённые
     ширины остались бы от прежней раскладки и разъехались бы с шапкой. */
  useEffect(() => { setPinned(null) }, [name, columnKeys, compact, layout.widths, layout.hidden])
  useLayoutEffect(() => {
    if (!heavy || pinned) return
    const head = scrollRef.current?.querySelector('thead tr')
    if (!head || head.children.length === 0) return
    setPinned([...head.children].map((th) => (th as HTMLElement).getBoundingClientRect().width))
  }, [heavy, pinned, items.length])

  /* Кто на самом деле прокручивается. У таблицы есть свой контейнер, но не на
     каждом экране цепочка высот доведена до него: на «Документах MDR»
     прокручивается страница, а контейнер вырастает на все строки. Виртуализатор,
     привязанный к невырастающему окну, считал бы, что видно всё, и не экономил
     бы ничего.

     Ищем ближайшего предка, чьё окно ограничено высотой экрана. scrollMargin —
     сколько над таблицей стоит своего (шапка, панель фильтров): без него
     видимое окно съезжает и строки рисуются не те. */
  const [scroller, setScroller] = useState<HTMLElement | null>(null)
  const [scrollMargin, setScrollMargin] = useState(0)
  useLayoutEffect(() => {
    const own = scrollRef.current
    if (!own || !heavy) return
    const bounded = (el: HTMLElement) => el.clientHeight > 0 && el.clientHeight <= window.innerHeight + 1
    let found: HTMLElement = own
    if (!bounded(own)) {
      for (let node = own.parentElement; node; node = node.parentElement) {
        if (/(auto|scroll|overlay)/.test(getComputedStyle(node).overflowY) && bounded(node)) { found = node; break }
      }
    }
    setScroller(found)
    setScrollMargin(found === own ? 0
      : own.getBoundingClientRect().top - found.getBoundingClientRect().top + found.scrollTop)
  }, [heavy, items.length, pinned])

  const virtualizing = heavy && pinned != null && scroller != null
  const virtualizer = useVirtualizer({
    count: virtualizing ? items.length : 0,
    getScrollElement: () => scroller,
    estimateSize: () => rh + 10,
    overscan: 12,
    scrollMargin,
  })
  const vRows = virtualizing ? virtualizer.getVirtualItems() : []
  const padTop = vRows.length ? vRows[0]!.start - scrollMargin : 0
  const padBottom = vRows.length ? virtualizer.getTotalSize() - (vRows[vRows.length - 1]!.end - scrollMargin) : 0
  /* Контейнер ещё не измерен — первый кадр, скрытая вкладка, окно нулевой
     высоты. Виртуализатору нечего показать, но таблица с данными не имеет
     права выглядеть пустой: рисуем первый экран, дальше он возьмёт своё. */
  const unmeasured = virtualizing && vRows.length === 0 && items.length > 0

  const Th = (c: SmartColumn<R>) => (
    <th
      key={c.key}
      data-col={c.key}
      draggable
      onDragStart={(e) => {
        if (resizing.current) { e.preventDefault(); return }
        e.dataTransfer.setData('text/plain', c.key)
        e.dataTransfer.effectAllowed = 'move'
      }}
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => {
        e.preventDefault()
        const from = e.dataTransfer.getData('text/plain')
        if (from) reorder(from, c.key)
      }}
      style={{
        position: 'relative',
        textAlign: c.align || 'left',
        fontFamily: 'var(--font-mono)',
        fontSize: 'var(--text-2xs)',
        fontWeight: 600,
        letterSpacing: 'var(--tracking-caps)',
        textTransform: 'uppercase',
        color: 'var(--text-subtle)',
        background: 'var(--surface-sunken)',
        padding: compact ? '6px 10px' : '8px 12px',
        borderBottom: '1px solid var(--border-strong)',
        width: layout.widths[c.key] ?? c.width,
        whiteSpace: 'nowrap',
        userSelect: 'none',
      }}
    >
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 5,
          cursor: c.sortable === false ? 'default' : 'pointer',
          justifyContent: c.align === 'right' ? 'flex-end' : c.align === 'center' ? 'center' : 'flex-start',
        }}
        {...clickable(() => clickSort(c))}
      >
        {c.header}
        {sort && sort.key === c.key ? (
          <span style={{ display: 'inline-flex', color: 'var(--accent-ink)' }}>
            <Icon name={sort.dir === 'asc' ? 'chevron-up' : 'chevron-down'} size={12} />
          </span>
        ) : null}
        {c.filterable ? (
          <span
            {...clickable((e) => {
              e.stopPropagation()
              setOpenFilter(openFilter === c.key ? null : c.key)
            })}
            style={{
              display: 'grid',
              placeItems: 'center',
              cursor: 'pointer',
              color: filters[c.key] ? 'var(--accent-ink)' : 'var(--text-subtle)',
            }}
          >
            <Icon name="list-filter" size={12} />
          </span>
        ) : null}
      </span>
      {openFilter === c.key ? (
        <FilterPopover
          col={c}
          rows={rows}
          active={filters[c.key]}
          onApply={(set) => applyFilter(c.key, set)}
          onClose={() => setOpenFilter(null)}
          lang={lang}
        />
      ) : null}
      {/* Граница столбца. Полоса широкая, чтобы курсор её ловил, а линия внутри
          тонкая — иначе шапка пестрит. Прежде полоса была полностью прозрачной:
          где граница, человек угадывал. */}
      <span
        role="presentation"
        className="lav-col-grip"
        data-resizing={gripKey === c.key ? '1' : undefined}
        onPointerDown={(e) => startResize(c.key, e)}
        style={{ position: 'absolute', top: 0, right: 0, bottom: 0, width: 7, cursor: 'col-resize', touchAction: 'none' }}
      />
    </th>
  )

  const GroupHead = (g: { v: CellValue; rows: R[] }, v?: VirtualItem) => {
    const gk = String(g.v)
    const open = !collapsed.has(gk)
    return (
      <tr
        key={'g-' + gk}
        className="tdms-rail"
        data-index={v?.index}
        ref={v ? virtualizer.measureElement : undefined}
        style={{ background: 'var(--surface-sunken)', cursor: 'pointer' }}
        onClick={() =>
          setCollapsed((s) => {
            const n = new Set(s)
            if (n.has(gk)) n.delete(gk)
            else n.add(gk)
            return n
          })
        }
      >
        <td
          colSpan={totalCols}
          style={{
            padding: compact ? '6px 12px' : '8px 14px',
            borderBottom: '1px solid var(--border-default)',
            borderTop: '1px solid var(--border-default)',
          }}
        >
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <span style={{ display: 'inline-flex', color: 'var(--text-muted)' }}>
              <Icon name={open ? 'chevron-down' : 'chevron-right'} size={14} />
            </span>
            <span style={{ font: 'var(--font-ui)', fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--text-strong)' }}>
              {lbl(gcol!, g.v)}
            </span>
            <span
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 10.5,
                color: 'var(--text-subtle)',
                background: 'var(--surface-card)',
                border: '1px solid var(--border-subtle)',
                borderRadius: 'var(--radius-sm)',
                padding: '0 6px',
              }}
            >
              {g.rows.length}
            </span>
          </span>
        </td>
      </tr>
    )
  }

  const Row = (r: R, v?: VirtualItem) => {
    const k = rowKey(r)
    const sel = hasSel && selectedKeys!.has(k)
    return (
      <tr
        key={k}
        className="smt-row"
        /* Высота строки меряется по факту: наименование переносится, и
           одинаковой её считать нельзя. */
        data-index={v?.index}
        ref={v ? virtualizer.measureElement : undefined}
        onClick={() => onRowClick && onRowClick(r)}
        style={{
          height: rh,
          cursor: onRowClick ? 'pointer' : 'default',
          background: sel ? 'var(--accent-soft)' : 'transparent',
          boxShadow: sel ? 'inset 2px 0 0 var(--accent)' : 'none',
        }}
      >
        {hasSel ? (
          <td
            style={{ ...tdBase, width: 36, paddingLeft: 12 }}
            onClick={(e) => {
              e.stopPropagation()
              onToggleRow!(k)
            }}
          >
            <Checkbox checked={!!sel} onChange={() => {}} />
          </td>
        ) : null}
        {cols.map((c) => (
          <td
            key={c.key}
            style={{
              ...tdBase,
              textAlign: c.align || 'left',
              fontFamily: c.mono ? 'var(--font-mono)' : undefined,
              whiteSpace: c.nowrap ? 'nowrap' : undefined,
              padding: compact ? '5px 10px' : '7px 12px',
            }}
          >
            {c.render ? c.render(r) : val(c, r) == null ? '—' : String(val(c, r))}
          </td>
        ))}
      </tr>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%' }}>
      {/* control bar */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '8px 14px',
          borderBottom: '1px solid var(--border-subtle)',
          background: 'var(--surface-card)',
          flexWrap: 'wrap',
        }}
      >
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            font: 'var(--font-eyebrow)',
            textTransform: 'uppercase',
            letterSpacing: 'var(--tracking-caps)',
            color: 'var(--text-subtle)',
          }}
        >
          <Icon name="group" size={13} />
          {t(lang, 'Группировка', 'Group by')}
        </span>
        <div
          style={{
            display: 'inline-flex',
            border: '1px solid var(--border-strong)',
            borderRadius: 'var(--radius-sm)',
            overflow: 'hidden',
          }}
        >
          <button onClick={() => setGroup(null)} className="smt-seg" data-on={!group ? '' : undefined} style={segBtn(!group)}>
            {t(lang, 'нет', 'none')}
          </button>
          {groupables.map((c) => (
            <button key={c.key} onClick={() => setGroup(group === c.key ? null : c.key)} className="smt-seg" data-on={group === c.key ? '' : undefined} style={segBtn(group === c.key)}>
              {c.header}
            </button>
          ))}
        </div>
        {Object.keys(filters).length ? (
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            {Object.keys(filters).map((k) => (
              <span
                key={k}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 5,
                  font: 'var(--font-ui)',
                  fontSize: 11.5,
                  color: 'var(--accent-ink)',
                  background: 'var(--accent-soft)',
                  border: '1px solid var(--accent-line)',
                  borderRadius: 'var(--radius-sm)',
                  padding: '1px 4px 1px 8px',
                }}
              >
                {colByKey(k)!.header}
                <button
                  className="smt-x"
                  onClick={() => applyFilter(k, null)}
                  style={{
                    display: 'grid',
                    placeItems: 'center',
                    width: 16,
                    height: 16,
                    border: 0,
                    background: 'transparent',
                    color: 'var(--accent-ink)',
                    cursor: 'pointer',
                  }}
                >
                  <Icon name="x" size={11} />
                </button>
              </span>
            ))}
            <button
              className="smt-clear"
              onClick={() => {
                // Снять весь отбор: на сервере — по колонке за раз, у себя — разом.
                if (serverFiltered) Object.keys(filters).forEach((k) => onFilter!(k, null))
                else setOwnFilters({})
              }}
              style={{
                font: 'var(--font-ui)',
                fontSize: 11.5,
                color: 'var(--text-link)',
                border: 0,
                background: 'transparent',
                cursor: 'pointer',
              }}
            >
              {t(lang, 'очистить', 'clear')}
            </button>
          </div>
        ) : null}
        {/* Состав столбцов — рядом с группировкой и плотностью: настройка того
            же рабочего места, что и они. */}
        <div style={{ position: 'relative', display: 'inline-flex', marginLeft: 'auto' }}>
          {/* Фон и цвет несут состояние «есть скрытые столбцы», но живут в
              классе по data-атрибуту, а не в инлайне: инлайновый фон глушил бы
              hover без !important. */}
          <button
            type="button"
            aria-expanded={openCols}
            onClick={() => setOpenCols(!openCols)}
            className="smt-ctl"
            data-on={layout.hidden.length ? '' : undefined}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
              padding: '4px 8px',
              border: '1px solid var(--border-strong)',
              borderRadius: 'var(--radius-sm)',
              font: 'var(--font-ui)',
              fontSize: 12,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            <Icon name="columns-3" size={13} />
            {t(lang, 'Колонки', 'Columns')}
            {layout.hidden.length ? (
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5 }}>
                {cols.length}/{ordered.length}
              </span>
            ) : null}
          </button>
          {openCols ? (
            <ColumnsPopover
              columns={ordered}
              hidden={layout.hidden}
              onToggle={toggleColumn}
              onReset={reset}
              onClose={() => setOpenCols(false)}
              lang={lang}
            />
          ) : null}
        </div>
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-subtle)' }}>
          {view.length}
          {view.length !== rows.length ? ' / ' + rows.length : ''} {t(lang, 'строк', 'rows')}
        </span>
      </div>

      {/* table */}
      <div ref={scrollRef} style={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: virtualizing || Object.keys(layout.widths).length ? 'fixed' : 'auto' }}>
          {/* Закреплённые ширины: при виртуализации расклад «по содержимому»
              считался бы по видимым строкам, и колонки прыгали бы на каждом
              шаге прокрутки. Ширины сняты с заголовка, пока в DOM были все
              строки, — то есть ровно те, что и были на экране. */}
          {virtualizing && pinned ? (
            <colgroup>{pinned.map((w, i) => <col key={i} style={{ width: w }} />)}</colgroup>
          ) : null}
          <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
            <tr>
              {hasSel ? (
                <th
                  style={{
                    width: 36,
                    background: 'var(--surface-sunken)',
                    borderBottom: '1px solid var(--border-strong)',
                    padding: '0 0 0 12px',
                  }}
                >
                  <Checkbox
                    checked={view.length > 0 && view.every((r) => selectedKeys!.has(rowKey(r)))}
                    indeterminate={view.some((r) => selectedKeys!.has(rowKey(r))) && !view.every((r) => selectedKeys!.has(rowKey(r)))}
                    onChange={(e) => onToggleAll && onToggleAll(e.target.checked)}
                  />
                </th>
              ) : null}
              {cols.map(Th)}
            </tr>
          </thead>
          <tbody>
            {view.length === 0 && loading ? (
              /* Заглушка держит высоту: строка «Загрузка…» занимает одну
                 высоту вместо двадцати, и всё, что ниже таблицы, подпрыгивает
                 на каждой смене страницы, а не только на первой загрузке.
                 Ширины столбцов настоящие — они заданы заголовком. */
              Array.from({ length: 8 }, (_, i) => (
                <tr key={'skel-' + i} aria-hidden style={{ borderBottom: 'var(--border-hairline) solid var(--border-subtle)' }}>
                  {Array.from({ length: totalCols }, (__, c) => (
                    <td key={c} style={{ padding: compact ? '7px 10px' : '10px 12px' }}>
                      {/* Разная длина полос — иначе заглушка читается как
                          отрисованная сетка, а не как ожидание. */}
                      <span className="skel-cell" style={{ width: [80, 55, 92, 68][(i + c) % 4] + '%', animationDelay: (i % 4) * 90 + 'ms' }} />
                    </td>
                  ))}
                </tr>
              ))
            ) : view.length === 0 ? (
              <tr>
                <td
                  colSpan={totalCols}
                  style={{
                    padding: '40px',
                    textAlign: 'center',
                    font: 'var(--font-ui)',
                    fontSize: 'var(--text-sm)',
                    color: 'var(--text-subtle)',
                  }}
                >
                  {emptyText || t(lang, 'Нет строк', 'No rows')}
                </td>
              </tr>
            ) : unmeasured ? (
              items.slice(0, 30).map((it) => (it.kind === 'group' ? GroupHead(it.g) : Row(it.r)))
            ) : virtualizing ? (
              /* В DOM живут только видимые единицы. Отступы сверху и снизу
                 держат высоту прокрутки: без них полоса прокрутки прыгала бы
                 на каждый шаг. */
              <>
                {padTop > 0 ? <tr aria-hidden style={{ height: padTop }}><td colSpan={totalCols} style={{ padding: 0, border: 0 }} /></tr> : null}
                {vRows.map((v) => {
                  const it = items[v.index]!
                  return it.kind === 'group' ? GroupHead(it.g, v) : Row(it.r, v)
                })}
                {padBottom > 0 ? <tr aria-hidden style={{ height: padBottom }}><td colSpan={totalCols} style={{ padding: 0, border: 0 }} /></tr> : null}
              </>
            ) : groups ? (
              groups.map((g) => (
                <Fragment key={String(g.v)}>
                  {GroupHead(g)}
                  {!collapsed.has(String(g.v)) ? g.rows.map((r) => Row(r)) : null}
                </Fragment>
              ))
            ) : (
              view.map((r) => Row(r))
            )}
          </tbody>
        </table>
      </div>
      {/* Строка реестра — главная цель нажатия во всём приложении, и до сих
          пор она на нажатие не отзывалась: общее правило `.tdms-rail` сюда не
          доходит, класс у строки только свой. Селектор с `tr` намеренно: без
          него у `:active` и `:hover` одинаковый вес, побеждает идущий ниже
          `:hover`, и отклик не виден, пока держишь кнопку.
          Наведение — под условие указателя, как везде: на касании оно липнет. */}
      <style>{`
        .smt-row { border-bottom: 1px solid var(--border-subtle); }
        @media (hover: hover) and (pointer: fine) {
          .smt-row:hover { background: var(--surface-hover) !important; }
        }
        tr.smt-row:active { background: var(--surface-active) !important; }

        /* Контролы над таблицей: сегменты группировки, чипы фильтров,
           «очистить», «Колонки». Нажимались без отклика. Переход фона гасит и
           мгновенный скачок при смене сегмента. !important у сегментов и
           крестика — их фон задан инлайном (у сегментов он несёт состояние);
           у «Колонки» фон вынесен в класс, и !important не нужен. Активному
           сегменту hover не положен: он уже выбран. */
        .smt-seg, .smt-ctl, .smt-x, .smt-clear {
          transition: transform var(--duration-press) var(--ease-out),
                      background var(--duration-fast) var(--ease-standard),
                      color var(--duration-fast) var(--ease-standard);
        }
        .smt-ctl { background: var(--surface-card); color: var(--text-muted); }
        .smt-ctl[data-on] { background: var(--accent-soft); color: var(--accent-ink); }
        .smt-seg:not(:disabled):active,
        .smt-ctl:not(:disabled):active,
        .smt-clear:not(:disabled):active { transform: scale(0.97); }
        /* Крестик 16px: на такой площади 0.97 не читается — сжатие сильнее. */
        .smt-x { border-radius: var(--radius-sm); }
        .smt-x:not(:disabled):active { transform: scale(0.94); }
        @media (hover: hover) and (pointer: fine) {
          .smt-seg:not([data-on]):hover { background: var(--surface-hover) !important; }
          .smt-ctl:not([data-on]):hover { background: var(--surface-hover); }
          .smt-x:hover { background: var(--accent-line) !important; }
          .smt-clear:hover { text-decoration: underline; }
        }
      `}</style>
    </div>
  )
}
const tdBase: CSSProperties = {
  borderBottom: '1px solid var(--border-subtle)',
  verticalAlign: 'middle',
  color: 'var(--text-body)',
  fontSize: 'var(--text-sm)',
}
function segBtn(on: boolean): CSSProperties {
  return {
    padding: '4px 10px',
    border: 0,
    borderLeft: '0',
    cursor: 'pointer',
    background: on ? 'var(--accent-soft)' : 'var(--surface-card)',
    color: on ? 'var(--accent-ink)' : 'var(--text-muted)',
    font: 'var(--font-ui)',
    fontSize: 12,
    fontWeight: on ? 600 : 400,
    whiteSpace: 'nowrap',
  }
}
